import React from 'react'
import Head from 'next/head'
import Pitch from '@src/components/DisposalPage/Pitch'
import FeatureList from '@src/components/DisposalPage/FeatureList'
import Approach from '@src/components/DisposalPage/Approach'
import CaseStudy from '@src/components/DisposalPage/CaseStudy'
import FAQ from '@src/components/DisposalPage/FAQ'
import SchemaMarkup from '@src/components/SchemaMarkup'
import RelatedServices from '@src/components/RelatedServices'
import Clients from '@src/pages/LandingPage/Clients'
import Testimonials from '@src/pages/LandingPage/Testimonials'
import junkTestimonials from '@src/constants/junkTestimonials'
import TrustBox from '@src/pages/common/components/TrustBox'
import TrustedByClients from '@src/components/DisposalPage/TrustedByClients'
import pageClasses from '@src/styles/junk-removal-page.module.css'
// eslint-disable-next-line import/extensions
import residentialCleanout from '../../json/residential-cleanout-data.json'

const residentialCleanoutServiceData = {
  name: 'Residential Cleanout Services',
  description:
    'Nationwide residential cleanout service for whole homes, single rooms, garages, basements, attics, and tenant move-outs — a crew brings the truck and the labor, carries everything out, and closes the job with a diversion report.',
  serviceType: 'Residential Cleanout',
  url: 'https://getdimension.com/residential-cleanout',
  provider: {
    name: 'Dimension',
    url: 'https://getdimension.com',
    address: {
      streetAddress: '2261 Market Street, #4061',
      addressLocality: 'San Francisco',
      addressRegion: 'CA',
      postalCode: '94114',
      addressCountry: 'US',
    },
    telephone: '+1 (415) 340-7182',
  },
  offers: [
    { name: 'Whole-Home Cleanout' },
    { name: 'Garage Cleanout' },
    { name: 'Basement and Attic Cleanout' },
    { name: 'Apartment and Tenant Move-Out Cleanout' },
    { name: 'Estate Cleanout' },
  ],
}

const residentialCleanoutFAQData = {
  questions: residentialCleanout.FAQContent.contentList.map((faq) => ({
    question: faq.heading.replace(/<[^>]*>/g, ''),
    answer: faq.textList.join(' '),
  })),
}

function ResidentialCleanout() {
  return (
    <>
      <Head>
        <title>Residential &amp; House Cleanout Services | Dimension</title>
        <meta
          name="description"
          content="Residential cleanout services nationwide — whole homes, garages, basements, attics, move-outs, and estates. Our crew does the lifting, hauling, and recycling."
        />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="canonical" href="https://getdimension.com/residential-cleanout" />
        <SchemaMarkup type="WebSite" />
        <SchemaMarkup type="Service" data={residentialCleanoutServiceData} />
        <SchemaMarkup type="FAQPage" data={residentialCleanoutFAQData} />
        <SchemaMarkup type="LocalBusiness" />
      </Head>
      <div className={pageClasses.root}>
        <Pitch
          variant="hazardous-waste"
          heroImage="junk"
          heading={residentialCleanout.pitch.heading}
          material={residentialCleanout.pitch.material}
          pitchText={residentialCleanout.pitch.pitchText}
          ctaLink={residentialCleanout.pitch.ctaLink}
          ctaText={residentialCleanout.pitch.ctaText}
          ctaHeading={residentialCleanout.pitch.ctaHeading}
          ctaSubtext={residentialCleanout.pitch.ctaSubtext}
          belowCta={<TrustBox isMobile isDark={false} />}
          belowPitch={<TrustedByClients />}
        />
        <Testimonials testimonials={junkTestimonials} />
        <Clients />
        <FeatureList {...residentialCleanout.solutions} />
        <FeatureList {...residentialCleanout.benefits} />
        <CaseStudy {...residentialCleanout.caseStudy} />
        <Approach {...residentialCleanout.approach} />
        <FAQ {...residentialCleanout.FAQContent} />
        <RelatedServices
          services={[
            {
              label: 'Property Cleanouts',
              href: '/property-cleanouts',
              description:
                'The hub for every kind of cleanout we run — homes, offices, estates, garages, and basements.',
            },
            {
              label: 'Furniture Removal',
              href: '/furniture-removal',
              description:
                'Sofas, wardrobes, and bed frames when a couple of pieces are the whole job.',
            },
            {
              label: 'Mattress Removal',
              href: '/mattress-removal',
              description:
                'The one item almost every home cleanout produces, taken apart and recycled rather than landfilled.',
            },
            {
              label: 'Dumpster Rental',
              href: '/dumpster-rental',
              description:
                'A container on the driveway for a cleanout you would rather do yourself over several days.',
            },
          ]}
        />
      </div>
    </>
  )
}

export default ResidentialCleanout
